<template>
  <InfoWindowPopup
    data-testid="infowindow-popup"
    @close-infowindow="handleCloseInfoWindow"
  >
    <template #tab-tray>
      <InfoWindowTabTray
        :tabs="tabs"
        :selected-tab="selectedTabIndex"
        @tab-selected="setSelectedTabIndex"
      />
    </template>
    <template #content>
      <div
        v-if="tab"
        data-testid="infowindow-popup-content"
      >
        <NorthpointInfoWindow
          v-if="[
            'industrialBuilding',
            'industrialProject',
            'acquisitionProject',
            'multiFamilyProject',
            'multiFamilyAcquisitionProject',
            'dataCenterProject',
            'overlay',
            'taxOverlay'
          ].includes(tab.content.type)"
          :info-window-content="tab.content"
        />
        <FeatureInfoWindow
          v-if="tab.content.type === 'feature'"
          :info-window-content="tab.content"
        />
        <CompetitiveBuildingInfoWindow
          v-if="[
            'competitiveBuilding',
            'competitiveBuildingComp',
            'omCompetitiveBuilding',
            'bulkCompetitiveBuilding'
          ].includes(tab.content.type)"
          :info-window-content="tab.content"
          @setInfoWindowToolbar="setToolbar"
        />
        <MultiFamilyBuildingInfoWindow
          v-if="tab.content.type === 'multiFamilyBuilding'"
          :info-window-content="tab.content"
          @setInfoWindowToolbar="setToolbar"
        />
        <DataCenterBuildingInfoWindow
          v-if="tab.content.type === 'dataCenterBuilding'"
          :info-window-content="tab.content"
          @setInfoWindowToolbar="setToolbar"
        />
        <CompetitivePortfolioInfoWindow
          v-if="[
            'competitivePortfolio',
            'omCompetitivePortfolio',
            'bulkCompetitivePortfolio'
          ].includes(tab.content.type)"
          :info-window-content="tab.content"
          @setInfoWindowToolbar="setToolbar"
        />
        <ParcelInfoWindow
          v-if="tab.content.type === 'parcel'"
          :info-window-contents="[tab.content]"
        />
        <VectorTileInfoWindow
          v-if="['vectorTile'].includes(tab.content.type)"
          :info-window-contents="[tab.content]"
          @setInfoWindowToolbar="setToolbar"
        />
        <WMTSTileInfoWindow
          v-if="tab.content.type === 'wmtsTile'"
          :info-window-content="tab.content"
        />
      </div>
      <div
        v-else
        class="loading"
        data-testid="infowindow-popup-loading"
      >
        Loading...
      </div>
    </template>
    <template #footer>
      <InfoWindowCoordinates
        v-if="shouldShowLocation"
        :location="location"
      />
      <InfoWindowToolbar
        v-if="toolbar"
        :toolbar-data="toolbar"
      />
    </template>
  </InfoWindowPopup>
</template>

<script>
// Components
import InfoWindowPopup from './InfoWindowPopup.vue';
import InfoWindowTabTray from './InfoWindowTabTray.vue';
import NorthpointInfoWindow from './NorthpointInfoWindow/NorthpointInfoWindow.vue';
import FeatureInfoWindow from './FeatureInfoWindow.vue';
import CompetitiveBuildingInfoWindow from './CompetitiveBuildingInfoWindow.vue';
import ParcelInfoWindow from './ParcelInfoWindow.vue';
import VectorTileInfoWindow from './VectorTileInfoWindow.vue';
import InfoWindowToolbar from '@/components/InfoWindow/sections/Toolbar/InfoWindowToolbar.vue';
import InfoWindowCoordinates from './InfoWindowCoordinates.vue';
import MultiFamilyBuildingInfoWindow from './MultiFamilyBuildingInfoWindow.vue';
import WMTSTileInfoWindow from './WMTSTileInfoWindow.vue';
import DataCenterBuildingInfoWindow from './DataCenterBuildingInfoWindow.vue';
import CompetitivePortfolioInfoWindow from './CompetitivePortfolioInfoWindow.vue';

// Tools & Services
import {MapService} from '@/services/MapService';
import {mapMutations, mapActions, mapState} from 'vuex';
import {computed} from 'vue';
import {Markers} from '@/services/MapService';
import {NO_IW_EDITABLE_FEATURES} from '@/services/OverlaysService/Feature';

/**
 * @typedef {import('@/store/baseInfowindow').InfoWindowUpdatePayload} InfoWindowUpdatePayload
 */

export default {
  name: 'InfoWindow',
  components: {
    InfoWindowPopup,
    InfoWindowTabTray,
    NorthpointInfoWindow,
    FeatureInfoWindow,
    CompetitiveBuildingInfoWindow,
    ParcelInfoWindow,
    VectorTileInfoWindow,
    InfoWindowToolbar,
    InfoWindowCoordinates,
    MultiFamilyBuildingInfoWindow,
    WMTSTileInfoWindow,
    DataCenterBuildingInfoWindow,
    CompetitivePortfolioInfoWindow,
  },
  provide() {
    return {
      contentType: computed(() => {
        const type = this.tab.content?.type;
        if (type === 'competitiveBuildingComp') {
          return this.tab.content.comps.find((comp) => comp.id === this.tab.content.selectedComp).type;
        }
        if (type === 'feature') {
          return this.tab.content.featureType;
        }
        return type;
      }),
    };
  },
  data() {
    return {
      location: null,
      toolbar: null,
      /** @type {InfoWindowUpdatePayload} */
      tab: null,
    };
  },
  computed: {
    ...mapState(['isEditableMap']),
    ...mapState('infowindow', ['infoWindowContents', 'selectedTabIndex']),
    tabs() {
      return this.infoWindowContents.map((tab) => tab.content);
    },
    shouldShowLocation() {
      if (this.tab?.content?.featureType === 'placeMarker') return true;
      const type = this.tab?.content?.type;
      const showLocationTypes = [
        'industrialBuilding',
        'multiFamilyBuilding',
        'dataCenterBuilding',
        'competitiveBuilding',
        'competitiveBuildingComp',
        'competitivePortfolio',
        'industrialProject',
        'acquisitionProject',
        'multiFamilyProject',
        'multiFamilyAcquisitionProject',
      ];
      return type && showLocationTypes.includes(type);
    },
  },
  watch: {
    infoWindowContents: {
      /** @type {import('vue').WatchCallback<InfoWindowUpdatePayload[]>} */
      handler: async function(newVal) {
        if (newVal?.length === 0) { // if there are no tabs, close the infowindow
          this.tab = null;
          this.setSelectedTabIndex(0);
          return;
        }
        this.setSelectedTabIndex(newVal?.length >= (this.selectedTabIndex + 1) ? this.selectedTabIndex : 0);

        const newTab = newVal?.[this.selectedTabIndex ?? 0] ?? null;

        const toolbarChanged =
            this.toolbar?.toolbarOptions?.length !== newTab?.content?.toolbar?.toolbarOptions?.length;
        const isEditableBuilding = (type) => ['omCompetitiveBuilding', 'omCompetitivePortfolio', 'bulkCompetitiveBuilding', 'bulkCompetitivePortfolio', 'competitiveBuilding', 'competitivePortfolio', 'dataCenterBuilding', 'multiFamilyBuilding'].includes(type);
        // HACK: BMAPS-2293 This is a workaround to ensure the tab is updated when the content changes if the child has
        // made a local copy of the content for editing.
        if (this.tab?.content?.id !== newTab?.content?.id) {
          this.tab?.content?.host?.setHighlight?.(false);
          this.tab = null;
          await this.$nextTick();
          this.tab = newTab;
          this.tab?.content?.host?.setHighlight?.(true);
          this.tab?.content?.host?.setEditing?.(true);
          this.updateSelectedContentId(this.tab?.content?.id);
        } else if (toolbarChanged && !isEditableBuilding(this.tab?.content?.type)) {
          // BUG: Checking for editable building because forcing remount causes issues with editing state in those
          // IW types
          // If the tab did not change but the toolbar updated, reset the entire tab without setting highlight/editing
          this.tab = null;
          await this.$nextTick();
          this.tab = newTab;
        }

        this.location = this.tab?.content?.location ?? null;
        this.toolbar = this.tab?.content?.toolbar ?? null;
      },
      immediate: true,
      deep: true,
    },
    selectedTabIndex: {
      handler: async function(newIndex, oldIndex) {
        this.tab?.content?.host?.setHighlight?.(false);
        this.tab?.content?.host?.setEditing?.(false);
        this.updateSelectedContentId(null);

        const currentTab = this.infoWindowContents[oldIndex];
        // If the current tab is a marker, make sure it is no longer draggable before switching tabs.
        if (Markers.exists(`${currentTab.content.type}s`, currentTab.content.id)) {
          Markers.setDraggable(`${currentTab.content.type}s`, currentTab.content.id, false);
        }

        // HACK: BMAPS-2293 Any component that makes a local copy of the tab content for editing will not see changes to
        // this.tab. Any component that may be editable also needs the opportunity to clean up after itself
        // Setting the tab to null and forcing one cycle ensures the tab is unmounted before being remounted
        this.tab = null;
        await this.$nextTick();
        this.tab = this.infoWindowContents[newIndex];
        this.tab?.content?.host?.setHighlight?.(true);
        this.tab?.content?.host?.setEditing?.(true);
        this.updateSelectedContentId(this.tab?.content?.id);
        const iwContent = this.infoWindowContents[newIndex];
        this.toolbar = iwContent?.content?.toolbar ?? null;
      },
    },
  },
  mounted() {
    MapService.addMapListener('closeNewInfoWindowHandler', 'click', async (evt) => {
      if (this.onCooldown) return;
      this.handleCloseInfoWindow();
    });
  },
  beforeUnmount() {
    MapService.removeMapListener('closeNewInfoWindowHandler');
    this.tab?.content?.host?.setHighlight?.(false);

    if (!NO_IW_EDITABLE_FEATURES.includes(this.tab?.content?.featureType)) {
      this.tab?.content?.host?.setEditing?.(false);
    }

    this.tab?.content?.host?.closeActions?.();
    this.updateSelectedContentId(null);
  },
  methods: {
    ...mapMutations('infowindow', ['updateSelectedContentId', 'setSelectedTabIndex']),
    ...mapActions('infowindow', ['closeInfoWindow']),
    handleCloseInfoWindow() {
      this.updateSelectedContentId(null);
      this.closeInfoWindow();
    },
    setToolbar(toolbar) {
      this.infoWindowContents[this.selectedTabIndex].content.toolbar = toolbar;
    },
  },
};
</script>

<style lang="scss" scoped>
.loading {
  padding: 3px 15px;
}
</style>

<!-- eslint-disable vue-scoped-css/enforce-style-type -->
<!-- eslint-disable vue-scoped-css/no-parsing-error -->
<style lang="scss">
/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type="number"] {
  appearance: textfield;
  -moz-appearance: textfield;
}
</style>
